import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Legend,
  Tooltip,
} from "chart.js";
import { pdf } from "@react-pdf/renderer";
import InsightPDF from "../../components/Insights/InsightPDF";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Legend,
  Tooltip
);

const InformeMarkdown = ({ content }) => {
  const chartRef = useRef(null);

  const {
    grafico,
    otras_secciones,
    kpis,
    alertas,
    titulo_informe,
    ...mainSections
  } = content;

  const handleDownloadPDF = async () => {
    if (!chartRef.current) return;

    const chartInstance = chartRef.current;
    const chartImage = chartInstance.toBase64Image();

    const pdfBlob = await pdf(
      <InsightPDF parsed={content} chartImage={chartImage} />
    ).toBlob();

    const blobUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${titulo_informe || "informe"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  };

  const renderTextBlock = (key, value) => {
    if (!value) return null;

    const formattedTitle = key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

    const renderValue = (val) => {
      if (Array.isArray(val)) {
        return (
          <ul className="list-disc list-inside mb-4 space-y-1">
            {val.map((item, idx) => (
              <li key={idx}>{renderValue(item)}</li>
            ))}
          </ul>
        );
      } else if (typeof val === "object" && val !== null) {
        return (
          <ul className="list-disc list-inside mb-4 space-y-1">
            {Object.entries(val).map(([subKey, subVal], subIdx) => (
              <li key={subIdx}>
                <strong>{subKey}:</strong> {renderValue(subVal)}
              </li>
            ))}
          </ul>
        );
      } else {
        return (
          <>
            <span>{String(val)}</span>
            <br />
          </>
        );
      }
    };

    return (
      <div key={key}>
        <h5 className="text-lg font-semibold mb-1">{formattedTitle}</h5>
        {renderValue(value)}
      </div>
    );
  };

  const renderKpis = () =>
    kpis &&
    typeof kpis === "object" && (
      <div className="mb-6">
        <h5 className="text-xl font-semibold mb-2">Indicadores Clave (KPIs)</h5>
        <div className="kpi-grid">
          {Object.entries(kpis).flatMap(([label, value]) => {
            if (
              typeof value === "object" &&
              value !== null &&
              !Array.isArray(value)
            ) {
              return Object.entries(value).map(([subKey, subVal]) => (
                <div key={`${label}-${subKey}`} className="kpi-card">
                  <p className="kpi-label">{`${label.replace(
                    /_/g,
                    " "
                  )} - ${subKey}`}</p>
                  <p className="kpi-value">{String(subVal)}</p>
                </div>
              ));
            }
            return (
              <div key={label} className="kpi-card">
                <p className="kpi-label">{label.replace(/_/g, " ")}</p>
                <p className="kpi-value">{String(value)}</p>
              </div>
            );
          })}
        </div>
      </div>
    );

  const renderAlertas = () =>
    alertas?.length > 0 && (
      <div className="mb-6">
        <h5 className="text-xl font-semibold mb-2 text-red-700">
          Alertas Detectadas
        </h5>
        <ul className="list-disc list-inside space-y-1 text-red-700">
          {alertas.map((alerta, index) => (
            <li key={index}>{alerta}</li>
          ))}
        </ul>
      </div>
    );

  return (
    <div className="mt-6">
      <div className="bg-white p-4 rounded shadow-md">
        <div className="flex justify-end mb-4">
          <button onClick={handleDownloadPDF} className="btn btn-download-insight">
            Descargar PDF
          </button>
        </div>
        <h4 className="text-2xl font-bold mb-4">{titulo_informe}</h4>

        {Object.entries(mainSections).map(([key, value]) =>
          [
            "grafico",
            "otras_secciones",
            "titulo_informe",
            "user",
            "date",
          ].includes(key)
            ? null
            : renderTextBlock(key, value)
        )}

        {grafico && (
          <>
            <h5 className="text-lg font-semibold mt-6 mb-2">
              Gráfico de Consumo Energético
            </h5>
            <div className="chart-container">
              <Line
                ref={chartRef}
                data={{ labels: grafico.labels, datasets: grafico.datasets }}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "top" } },
                }}
              />
            </div>
          </>
        )}

        {otras_secciones &&
          Object.entries(otras_secciones).map(([key, value]) =>
            renderTextBlock(key, value)
          )}

        {renderKpis()}
        {renderAlertas()}
      </div>
    </div>
  );
};

export default InformeMarkdown;
